/**
 * Beta Feedback — full-page form for users to report bugs and submit feedback.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ArrowLeft, Send, Bug, Lightbulb, MessageCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { projectId, publicAnonKey } from '/utils/supabase/info';
import { useAuth } from '../lib/auth-context';
import { StockHeroStrip, WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';

type FeedbackType = 'bug' | 'feature' | 'general';

function toBackendType(t: string): 'bug' | 'feature' | 'general' {
  if (t === 'bug') return 'bug';
  if (t === 'feature') return 'feature';
  return 'general';
}

export default function Feedback() {
  const { user, accessToken } = useAuth();
  const navigate = useNavigate();
  const [type, setType] = useState<FeedbackType>('general');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error('Please enter your feedback');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/feedback`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${publicAnonKey}`,
            ...(accessToken ? { 'X-User-Token': accessToken } : {}),
          },
          body: JSON.stringify({
            type: toBackendType(type),
            message: message.trim().slice(0, 5000),
            userEmail: user?.email ?? '',
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            url: window.location.href,
          }),
        }
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error ?? `Request failed: ${res.status}`);
      }

      setSubmitted(true);
      toast.success('Thank you! Your feedback has been submitted.');
    } catch (error) {
      console.error('Feedback submission error:', error);
      toast.error('Failed to submit. Please try again or use Contact Support.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <WarmMarketingBackdrop>
      <div className="min-h-screen">
        <div className="mx-auto max-w-md px-4 py-12">
          <StockHeroStrip stockKey="handsWalking" alt="Illustrative imagery" />
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Card className="border-violet-100/90 bg-white/95 p-8 text-center shadow-lg">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-xl font-medium mb-2">Feedback submitted</h2>
            <p className="text-neutral-600 dark:text-neutral-400 text-sm mb-6">
              We read every submission. Thank you for helping improve TENSION.
            </p>
            <Button onClick={() => navigate('/settings')}>Return to Settings</Button>
          </Card>
        </div>
      </div>
      </WarmMarketingBackdrop>
    );
  }

  const types: { value: FeedbackType; label: string; icon: typeof Bug }[] = [
    { value: 'bug', label: 'Bug report', icon: Bug },
    { value: 'feature', label: 'Feature idea', icon: Lightbulb },
    { value: 'general', label: 'General feedback', icon: MessageCircle },
  ];

  return (
    <WarmMarketingBackdrop>
    <div className="min-h-screen">
      <div className="mx-auto max-w-lg px-4 py-8">
        <StockHeroStrip stockKey="coupleKitchen" alt="Illustrative imagery" />
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <Card className="border-violet-100/90 bg-white/95 p-6 shadow-lg">
          <h1 className="text-xl font-semibold mb-1">Beta Feedback</h1>
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-6">
            Report a bug, suggest a feature, or share your experience.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label className="mb-2 block">Type</Label>
              <div className="flex gap-2 flex-wrap">
                {types.map((t) => {
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => setType(t.value)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm transition-colors ${
                        type === t.value
                          ? 'bg-violet-600 text-white border-violet-600'
                          : 'border-neutral-300 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label htmlFor="feedback-message">Your feedback *</Label>
              <Textarea
                id="feedback-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={
                  type === 'bug'
                    ? 'Describe the bug and steps to reproduce...'
                    : type === 'feature'
                    ? "Describe the feature you'd like to see..."
                    : 'Share your feedback...'
                }
                rows={6}
                required
                className="mt-2"
              />
              <p className="text-xs text-neutral-500 mt-1">{message.length}/5000 characters</p>
            </div>

            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => navigate(-1)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || !message.trim()} className="flex-1 gap-2">
                <Send className="w-4 h-4" />
                {submitting ? 'Sending...' : 'Submit'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
    </WarmMarketingBackdrop>
  );
}
